import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { AiChatService } from '@app/core/services/ai-chat.service';
import { AiChatMessageItem } from '@app/core/services/ai-chat.types';

@Component({
    selector: 'tt-ai-chat-conversation',
    templateUrl: './ai-chat-conversation.component.html',
    styleUrls: ['./ai-chat-conversation.component.css'],
    standalone: false,
})
export class AiChatConversationComponent implements AfterViewInit, OnChanges {
    private readonly bottomThresholdPx = 24;

    private readonly pageSize = 20;

    private readonly maximumRenderedMessages = 40;

    private readonly pageLoadThresholdPx = 160;

    private readonly initialScrollSettleMs = 300;

    private readonly historyAnchorSettleMs = 500;

    private readonly messageHeights = new Map<string, number>();

    private followBottom = true;

    private userScrollIntent = false;

    private initialScrollPending = false;

    private scrollQueued = false;

    private queuedScrollForce = false;

    private initialScrollReleaseTimer?: ReturnType<typeof setTimeout>;

    private historyAnchorReleaseTimer?: ReturnType<typeof setTimeout>;

    private historyAnchor?: { messageKeyno: string; offset: number };

    private initializedChatKeyno?: string;

    public loadingOlder = false;

    public loadingNewer = false;

    private hasOlder = false;

    private hasNewer = false;

    @ViewChild('viewport')
    private viewport?: ElementRef<HTMLElement>;

    @ViewChildren('messageRow')
    private messageRows?: QueryList<ElementRef<HTMLElement>>;

    @Input()
    public chatKeyno?: string;

    @Input()
    public messages: AiChatMessageItem[] = [];

    @Input()
    public loading = false;

    @Input()
    public sending = false;

    public renderedMessages: AiChatMessageItem[] = [];

    public topSpacerHeight = 0;

    public bottomSpacerHeight = 0;

    constructor(
        private aiChat: AiChatService,
        private cdr: ChangeDetectorRef,
    ) {}

    public trackMessage(_index: number, message: AiChatMessageItem): string {
        return message.ai_message_keyno;
    }

    public onMessageContentResized(): void {
        this.captureMessageHeights();
        this.restoreHistoryAnchor();
        this.queueScrollToBottom(this.initialScrollPending);
    }

    public onScroll(event: Event): void {
        if (this.initialScrollPending) return;

        const viewport = event.target as HTMLElement;
        const readerNavigating = this.userScrollIntent || !this.followBottom;
        if (readerNavigating) {
            this.followBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= this.bottomThresholdPx;
        }
        this.userScrollIntent = false;

        const distanceFromTopSpacer = viewport.scrollTop - this.topSpacerHeight;
        const distanceToBottomSpacer = viewport.scrollHeight - this.bottomSpacerHeight - viewport.scrollTop - viewport.clientHeight;

        if ((this.topSpacerHeight > 0 && distanceFromTopSpacer <= this.pageLoadThresholdPx) || viewport.scrollTop <= this.pageLoadThresholdPx) {
            void this.loadOlderMessages();
        } else if (this.bottomSpacerHeight > 0 && distanceToBottomSpacer <= this.pageLoadThresholdPx) {
            void this.loadNewerMessages();
        }
    }

    public cancelInitialScroll(): void {
        this.clearInitialScrollReleaseTimer();
        this.clearHistoryAnchor();
        this.initialScrollPending = false;
    }

    /** Marks the next scroll event as an intentional reader navigation. */
    public onUserScrollIntent(): void {
        this.userScrollIntent = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['chatKeyno']) this.resetConversationWindow();
        if (changes['messages']) this.initializeOrSynchronizeMessages();
        if (changes['sending']?.currentValue === true && changes['sending'].previousValue !== true) this.followBottom = true;
        if (changes['chatKeyno'] || changes['messages'] || changes['sending'] || changes['loading']) this.queueScrollToBottom(this.initialScrollPending);
    }

    ngAfterViewInit(): void {
        this.queueScrollToBottom(this.initialScrollPending);
    }

    private resetConversationWindow(): void {
        this.initializedChatKeyno = undefined;
        this.renderedMessages = [];
        this.topSpacerHeight = 0;
        this.bottomSpacerHeight = 0;
        this.hasOlder = false;
        this.hasNewer = false;
        this.loadingOlder = false;
        this.loadingNewer = false;
        this.followBottom = true;
        this.userScrollIntent = false;
        this.initialScrollPending = true;
        this.clearInitialScrollReleaseTimer();
        this.clearHistoryAnchor();
        this.messageHeights.clear();
    }

    private initializeOrSynchronizeMessages(): void {
        const chatKeyno = this.getActiveChatKeyno();
        if (!chatKeyno) return;

        if (this.initializedChatKeyno !== chatKeyno || this.renderedMessages.length === 0) {
            this.initializedChatKeyno = chatKeyno;
            this.hasOlder = this.messages.length > this.pageSize;
            this.renderedMessages = this.messages.slice(-this.pageSize);
            return;
        }

        const messagesByKeyno = new Map(this.messages.map((message) => [message.ai_message_keyno, message]));
        this.renderedMessages = this.renderedMessages.map((message) => messagesByKeyno.get(message.ai_message_keyno) ?? message);
        if (this.bottomSpacerHeight > 0 || this.renderedMessages.length === 0) return;

        const newestRenderedMessage = this.renderedMessages[this.renderedMessages.length - 1];
        const newMessages = this.messages.filter((message) => !newestRenderedMessage || this.compareMessages(message, newestRenderedMessage) > 0);
        if (newMessages.length) this.renderedMessages = [...this.renderedMessages, ...newMessages];
    }

    private async loadOlderMessages(): Promise<void> {
        const oldestMessage = this.renderedMessages[0];
        const viewport = this.viewport?.nativeElement;
        const chatKeyno = this.getActiveChatKeyno();
        if (!chatKeyno || !oldestMessage || !viewport || !this.hasOlder || this.loadingOlder) return;

        this.followBottom = false;
        this.captureHistoryAnchor(oldestMessage.ai_message_keyno);
        this.loadingOlder = true;
        const previousScrollHeight = viewport.scrollHeight;
        const previousScrollTop = viewport.scrollTop;

        try {
            const page = await this.aiChat.getChatMessagePage({
                ai_chat_keyno: chatKeyno,
                before_reg_datetime: oldestMessage.reg_datetime,
                before_ai_message_keyno: oldestMessage.ai_message_keyno,
                num_of_rows: this.pageSize + 1,
            });
            const messages = page.slice(-this.pageSize);
            this.hasOlder = page.length > this.pageSize;
            if (!messages.length) return;

            this.topSpacerHeight = Math.max(0, this.topSpacerHeight - this.getMessagesHeight(messages));
            this.renderedMessages = [...messages, ...this.renderedMessages];
            this.cdr.detectChanges();

            requestAnimationFrame(() => {
                viewport.scrollTop = previousScrollTop + viewport.scrollHeight - previousScrollHeight;
                this.restoreHistoryAnchor();
                this.scheduleHistoryAnchorRelease();
                this.trimRenderedMessagesFromBottom();
            });
        } finally {
            this.loadingOlder = false;
            this.cdr.markForCheck();
        }
    }

    private async loadNewerMessages(): Promise<void> {
        const newestMessage = this.renderedMessages[this.renderedMessages.length - 1];
        const viewport = this.viewport?.nativeElement;
        const chatKeyno = this.getActiveChatKeyno();
        if (!chatKeyno || !newestMessage || !viewport || !this.hasNewer || this.loadingNewer) return;

        this.loadingNewer = true;
        try {
            const page = await this.aiChat.getChatMessagePage({
                ai_chat_keyno: chatKeyno,
                after_reg_datetime: newestMessage.reg_datetime,
                after_ai_message_keyno: newestMessage.ai_message_keyno,
                num_of_rows: this.pageSize + 1,
            });
            const messages = page.slice(0, this.pageSize);
            this.hasNewer = page.length > this.pageSize;
            if (!messages.length) return;

            this.bottomSpacerHeight = Math.max(0, this.bottomSpacerHeight - this.getMessagesHeight(messages));
            this.renderedMessages = [...this.renderedMessages, ...messages];
            this.cdr.detectChanges();

            requestAnimationFrame(() => {
                this.trimRenderedMessagesFromTop();
            });
        } finally {
            this.loadingNewer = false;
            this.cdr.markForCheck();
        }
    }

    private trimRenderedMessagesFromBottom(): void {
        const count = this.renderedMessages.length - this.maximumRenderedMessages;
        if (count <= 0) return;
        const removedMessages = this.renderedMessages.slice(-count);
        this.captureMessageHeights();
        this.bottomSpacerHeight += this.getMessagesHeight(removedMessages);
        this.hasNewer = true;
        this.renderedMessages = this.renderedMessages.slice(0, -count);
        this.cdr.detectChanges();
    }

    private trimRenderedMessagesFromTop(): void {
        const count = this.renderedMessages.length - this.maximumRenderedMessages;
        if (count <= 0) return;
        const removedMessages = this.renderedMessages.slice(0, count);
        this.captureMessageHeights();
        this.topSpacerHeight += this.getMessagesHeight(removedMessages);
        this.hasOlder = true;
        this.renderedMessages = this.renderedMessages.slice(count);
        this.cdr.detectChanges();
    }

    private captureMessageHeights(): void {
        const gap = Number.parseFloat(getComputedStyle(this.viewport?.nativeElement ?? document.documentElement).rowGap) || 0;
        this.messageRows?.forEach((row) => {
            const keyno = row.nativeElement.dataset['messageKeyno'];
            if (keyno) this.messageHeights.set(keyno, row.nativeElement.getBoundingClientRect().height + gap);
        });
    }

    private getMessagesHeight(messages: AiChatMessageItem[]): number {
        return messages.reduce((height, message) => height + (this.messageHeights.get(message.ai_message_keyno) ?? 0), 0);
    }

    private compareMessages(left: AiChatMessageItem, right: AiChatMessageItem): number {
        const datetimeComparison = left.reg_datetime.localeCompare(right.reg_datetime);
        return datetimeComparison || Number(left.ai_message_keyno) - Number(right.ai_message_keyno);
    }

    private getActiveChatKeyno(): `${number}` | undefined {
        return (this.chatKeyno ?? this.messages[0]?.ai_chat_keyno) as `${number}` | undefined;
    }

    private queueScrollToBottom(force = false): void {
        if (!this.followBottom && !force) return;
        if (this.scrollQueued) {
            this.queuedScrollForce ||= force;
            return;
        }

        this.scrollQueued = true;
        this.queuedScrollForce = force;
        requestAnimationFrame(() => {
            const queuedForce = this.queuedScrollForce;
            this.scrollQueued = false;
            this.queuedScrollForce = false;
            const viewport = this.viewport?.nativeElement;
            if (viewport && (this.followBottom || queuedForce)) viewport.scrollTop = viewport.scrollHeight;
            if (queuedForce) this.scheduleInitialScrollRelease();
        });
    }

    /** Releases forced bottom positioning after iframe content has stopped changing. */
    private scheduleInitialScrollRelease(): void {
        this.clearInitialScrollReleaseTimer();
        this.initialScrollReleaseTimer = setTimeout(() => {
            this.initialScrollPending = false;
            this.initialScrollReleaseTimer = undefined;
        }, this.initialScrollSettleMs);
    }

    private clearInitialScrollReleaseTimer(): void {
        if (this.initialScrollReleaseTimer !== undefined) {
            clearTimeout(this.initialScrollReleaseTimer);
            this.initialScrollReleaseTimer = undefined;
        }
    }

    /** Captures the current reader position so late iframe heights cannot move it while history renders. */
    private captureHistoryAnchor(messageKeyno: string): void {
        const viewport = this.viewport?.nativeElement;
        const row = this.messageRows?.find((item) => item.nativeElement.dataset['messageKeyno'] === messageKeyno)?.nativeElement;
        if (!viewport || !row) return;

        this.historyAnchor = {
            messageKeyno,
            offset: row.getBoundingClientRect().top - viewport.getBoundingClientRect().top + Math.min(120, viewport.clientHeight * 0.15),
        };
    }

    private restoreHistoryAnchor(): void {
        const viewport = this.viewport?.nativeElement;
        const anchor = this.historyAnchor;
        const row = anchor ? this.messageRows?.find((item) => item.nativeElement.dataset['messageKeyno'] === anchor.messageKeyno)?.nativeElement : undefined;
        if (!viewport || !anchor || !row) return;

        viewport.scrollTop += row.getBoundingClientRect().top - viewport.getBoundingClientRect().top - anchor.offset;
        this.scheduleHistoryAnchorRelease();
    }

    private scheduleHistoryAnchorRelease(): void {
        if (!this.historyAnchor) return;
        if (this.historyAnchorReleaseTimer !== undefined) clearTimeout(this.historyAnchorReleaseTimer);
        this.historyAnchorReleaseTimer = setTimeout(() => this.clearHistoryAnchor(), this.historyAnchorSettleMs);
    }

    private clearHistoryAnchor(): void {
        if (this.historyAnchorReleaseTimer !== undefined) {
            clearTimeout(this.historyAnchorReleaseTimer);
            this.historyAnchorReleaseTimer = undefined;
        }
        this.historyAnchor = undefined;
    }
}
